/**
 * Exports Base44 Product records to a CSV formatted for LitCommerce's
 * "CSV file as Main Store" product import.
 *
 * LitCommerce has no public push API — instead you upload a CSV (or Google
 * Sheets URL) and it treats that file as the source catalog, then publishes
 * to your connected marketplaces (eBay, Amazon, Etsy, etc.). LitCommerce
 * auto-maps its fields to your column headers; required fields are Product ID
 * and Product Name. Multiple images go in SEPARATE columns (Image 1, Image 2…).
 *
 * Workflow for the admin: download this CSV → in LitCommerce choose
 * "Add CSV file" as the Main Store → upload → map any unmatched columns →
 * Start Import → then publish to channels.
 */

const COLUMNS = [
  // ── IDENTITY (required: Product ID, Product Name) ──────────────────
  ['Product ID',   p => p.id],
  ['Product Name', p => (p.name || '').slice(0, 200)],
  ['SKU',          p => p.sku || ''],
  ['Parent SKU',   p => ''], // simple products only — variations not modelled here
  ['Variation Name', p => p.variation_name || ''],

  // ── DESCRIPTION ────────────────────────────────────────────────────
  ['Description',  p => p.description || ''],

  // ── PRICING / INVENTORY ────────────────────────────────────────────
  ['Price',        p => (typeof p.price === 'number' ? p.price.toFixed(2) : '')],
  ['Currency',     () => 'GBP'],
  ['Quantity',     p => {
    const q = p.stock_quantity;
    if (typeof q === 'number' && q > 0) return String(q);
    return p.in_stock === false ? '0' : '5'; // untracked → default available
  }],

  // ── ATTRIBUTES ─────────────────────────────────────────────────────
  ['Brand',        () => 'Motion Reality'],
  ['Condition',    () => 'New'],
  ['Category',     p => p.category || ''],
  ['GTIN',         p => p.gtin || ''],
  ['Option Name',  p => p.option_name || ''],
  ['Option Value', p => p.option_value || ''],

  // ── WEIGHT ──────────────────────────────────────────────────────────
  ['Weight',       p => (typeof p.weight === 'number' ? String(p.weight) : '')],
  ['Weight Unit',  p => p.weight_unit || 'g'],

  // ── IMAGES (each URL in its own column) ────────────────────────────
  ['Image 1',      p => p.image_url || ''],
  ['Image 2',      p => p.image_url_2 || ''],
  ['Image 3',      p => p.image_url_3 || ''],
  ['Image 4',      p => p.image_url_4 || ''],

  // ── STATUS ──────────────────────────────────────────────────────────
  ['Status',       p => (p.published === false ? 'Inactive' : 'Active')],
];

function escapeCsv(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/**
 * Builds and downloads a LitCommerce Main Store product CSV.
 * Only published products are included (hidden/archived items shouldn't go
 * to marketplaces). Returns { exported, skipped } for caller feedback.
 */
export function exportProductsToLitCommerceCsv(products) {
  const eligible = (products || []).filter(p => p.published !== false);
  const skipped = (products || []).length - eligible.length;

  const headers = COLUMNS.map(([heading]) => escapeCsv(heading)).join(',');
  const rows = eligible.map(p =>
    COLUMNS.map(([, mapper]) => escapeCsv(mapper(p))).join(',')
  );

  const csv = [headers, ...rows].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `litcommerce_products_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  return { exported: eligible.length, skipped };
}